import {
  Box,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Grid,
  Popover,
  Dialog,
  DialogTitle,
  DialogActions,
  Typography,
  TextField,
  Autocomplete,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { styled } from '@mui/material/styles'

export const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== '$hasStaff' && prop !== '$hasEndTime',
})<{ $hasStaff: boolean; $hasEndTime: boolean }>(
  ({ theme, $hasStaff = false, $hasEndTime = false }) => ({
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: 'rgba(255, 255, 255, 0.8)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '100%',
    height:
      $hasEndTime && $hasStaff
        ? '160px'
        : $hasEndTime
        ? '136px'
        : $hasStaff
        ? '146px'
        : '120px',
    padding: theme.spacing(0.8),
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0.7),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.6),
    },
    '&:hover': {
      boxShadow: '0 2px 6px rgba(0,0,0,0.05)',
      transform: 'translateY(-1px)',
    },
  })
)

export const StatusDot = styled('span')<{ $isGreen: boolean }>(
  ({ theme, $isGreen }) => ({
    display: 'inline-block',
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: $isGreen
      ? theme.palette.primary.main
      : theme.palette.success.light,
    marginRight: 6,
  })
)

export const RoomHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
  minHeight: 28,
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(0.4),
    minHeight: 26,
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(0.3),
    minHeight: 24,
  },
}))

export const RoomName = styled(Box)(({ theme }) => ({
  userSelect: 'none',
  transition: 'color 0.15s',
  display: 'flex',
  alignItems: 'center',
  fontWeight: 700,
  fontSize: 14,
  color: theme.palette.primary.main,
  gap: theme.spacing(0.6),
  [theme.breakpoints.down('md')]: {
    fontSize: 13,
    gap: theme.spacing(0.5),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 12,
    gap: theme.spacing(0.3),
  },
}))

export const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== '$bgColor',
})<{ $bgColor: string }>(({ $bgColor }) => ({
  background: $bgColor,
  color: 'white',
  fontWeight: 600,
  fontSize: 11,
  height: 20,
  borderRadius: 6,
  cursor: 'pointer',
  '.MuiChip-label': {
    paddingLeft: 6,
    paddingRight: 6,
  },
}))

export const ActionButton = styled(IconButton)({
  padding: 4,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
})

export const UntilIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: 12,
  gap: theme.spacing(0.3),
  [theme.breakpoints.down('md')]: {
    fontSize: 11.5,
    gap: theme.spacing(0.25),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 11,
    gap: theme.spacing(0.2),
  },
}))

export const EmptyRoomBox = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  opacity: 0.7,
})

export const StyledMenu = styled(Menu)({
  '& .MuiPaper-root': {
    borderRadius: 10,
    minWidth: 140,
    boxShadow: `
      0 0 0 1px rgba(0, 0, 0, 0.03),
      0 4px 24px rgba(0, 0, 0, 0.06),
      0 8px 48px rgba(0, 0, 0, 0.04)
    `,
    backdropFilter: 'blur(20px)',
    background: 'rgba(255, 255, 255, 0.98)',
    marginTop: 6,
    '& .MuiList-root': {
      padding: '6px 0',
    },
  },
})

StyledMenu.defaultProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  transitionDuration: 200,
  elevation: 0,
}

export const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== '$highlight',
})<{ $highlight?: boolean }>(({ theme, $highlight }) => ({
  fontSize: 13.5,
  fontWeight: $highlight ? 600 : 500,
  padding: '8px 14px',
  margin: '0 6px',
  borderRadius: 8,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  letterSpacing: '-0.01em',
  color: theme.palette.primary.main,
  backgroundColor: $highlight
    ? `${theme.palette.primary.main}10`
    : 'transparent',
  border: $highlight
    ? `1px solid ${theme.palette.primary.main}30`
    : '1px solid transparent',
  '&:hover': {
    backgroundColor: $highlight
      ? `${theme.palette.primary.main}20`
      : 'rgba(0, 0, 0, 0.03)',
    transform: 'translateY(-0.5px)',
  },
  '&:active': {
    backgroundColor: $highlight
      ? `${theme.palette.primary.main}30`
      : 'rgba(0, 0, 0, 0.06)',
    transform: 'translateY(0)',
  },
}))

export const AddPatientButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 16px',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: 'none',
    background: theme.palette.primary.dark,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 32,
    padding: '6px 8px',
  },
}))

export const NursesSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.6),
  flexWrap: 'wrap',
  fontSize: 11,
  color: theme.palette.primary.main,
  fontWeight: 600,
  transition: 'all 0.2s ease',
  '&:hover': {
    color: theme.palette.primary.dark,
    transform: 'translateY(-0.5px)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}))

export const EditStaff = styled(Box)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.primary.main,
  fontWeight: 600,
  cursor: 'pointer',
}))

export const NurseChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: theme.palette.success.dark,
  fontWeight: 500,
  fontSize: 10,
  height: 22,
  borderRadius: 11,
  border: `1px solid rgba(76, 175, 80, 0.2)`,
  '.MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
  '&:hover': {
    backgroundColor: 'rgba(76, 175, 80, 0.15)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
  },
}))

export const MoreNursesChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(156, 39, 176, 0.1)',
  color: theme.palette.secondary.dark,
  fontWeight: 500,
  fontSize: 11,
  height: 22,
  borderRadius: 11,
  border: `1px solid rgba(156, 39, 176, 0.2)`,
  cursor: 'pointer',
  '.MuiChip-label': {
    paddingLeft: 8,
    paddingRight: 8,
  },
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(156, 39, 176, 0.15)',
    borderColor: 'rgba(156, 39, 176, 0.3)',
    transform: 'scale(1.02)',
  },
}))

export const RoomGridContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  isolation: 'isolate',

  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.1)',
    borderRadius: '3px',
    '&:hover': {
      background: 'rgba(0,0,0,0.15)',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(0,0,0,0.1) transparent',
  '&::-webkit-scrollbar-thumb:vertical': {
    minHeight: '30px',
  },
  '&::-webkit-scrollbar-thumb:vertical:inactive': {
    background: 'transparent',
  },
}))

export const RoomGridContent = styled(Grid)(() => ({
  width: '100%',
}))

export const BottomCardInfo = styled(Box)({
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  gap: 4,
  flexShrink: 0,
})

export const ExpectedEnd = styled(Box)(({ theme }) => ({
  fontSize: 11,
  color: theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  fontWeight: 500,
}))

export const StyledGrid = styled(Grid)({
  minWidth: '180px',
})

StyledGrid.defaultProps = {
  item: true,
  xs: 12,
  sm: 6,
  md: 4,
  lg: 2.4,
  xl: 2,
}

export const StyledPopover = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    minWidth: 200,
    maxWidth: 280,
    border: '1px solid',
    borderColor: theme.palette.divider,
  },
}))

StyledPopover.defaultProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'center',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'center',
  },
}

// New styled components for popover content
export const PopoverContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}))

export const PopoverTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  fontWeight: 600,
  color: theme.palette.primary.main,
  fontSize: 14,
}))

export const StaffList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.8),
}))

export const StaffItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.8),
  borderRadius: theme.spacing(2),
  backgroundColor: 'rgba(76, 175, 80, 0.05)',
  border: '1px solid rgba(76, 175, 80, 0.1)',
}))

export const StaffStatusDot = styled(Box)(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  marginRight: theme.spacing(1),
}))

export const StaffName = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.text.primary,
}))

// Dialog components
export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  },
}))

export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: theme.spacing(1),
}))

export const DialogTitleText = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 18,
}))

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
  },
}))

export const StyledAutocompleteChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: theme.palette.success.dark,
  '& .MuiChip-deleteIcon': {
    color: theme.palette.success.dark,
    '&:hover': {
      color: theme.palette.success.main,
    },
  },
}))

export const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  marginTop: theme.spacing(1),
  '& .MuiAutocomplete-tag': {
    backgroundColor: 'rgba(76, 175, 80, 0.1)',
    color: theme.palette.success.dark,
  },
})) as typeof Autocomplete

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(2),
}))

export const CancelButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
}))

export const SaveButton = styled(Button)(() => ({
  fontWeight: 600,
}))

export const ExpectedEndIcon = styled(AccessTimeIcon)(() => ({
  fontSize: 12,
  opacity: 0.6,
}))

export const NoRoomsText = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: '-0.01em',
  color: theme.palette.primary.main,
}))

export const NoRoomsContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  gap: 16,
})
